import { Text } from "../../../components/general/Text";
import { css } from "@emotion/react";
import { capitalize, cloneDeep, partition } from "lodash-es";
import { NAV_LINKS, WRAPPED_ABI } from "../../../config/consts";
import { Button } from "../../../components/general/Button/Button";
import { ConfirmInWallet } from "../../../components/Modal/instance/Allowance/ConfirmInWallet";
import { ProcessingTransaction } from "../../../components/Modal/instance/Allowance/ProcessingTransaction";
import { IconWrapper, LearnMoreLink } from "../WrappingQuote";
import { Chain, useWalletClient, WalletClient } from "wagmi";
import { useTokenListByChain } from "../../../hooks/useTokenListByChain";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ReactComponent as ArrowPointingRight } from "../../../img/trade-interface/ArrowPointingRight.svg";
import { getTokenIcon } from "../../../utils/tokenIconUtils";
import { Token } from "../../../store/global/TokenEnum";
import { getWrappedSymbolForChain } from "../../../utils/chainUtils";
import { GlobalStore } from "../../../store/global/GlobalStore";
import { getColourForToken, getNameForToken } from "../../../utils/tokenListUtils";
import { TokenList } from "../../../store/global/getAllTokenLists";
import { Account, Hash, parseUnits, Transport } from "viem";
import { Amount } from "../../../types/amount";
import { getContract, waitForTransaction } from "wagmi/actions";
import { getTokenAddressBySymbol } from "../../../utils/orderUtils";
import { getBalances } from "../../../store/global/getBalances";
import {
  getAmountNeededFromQuote,
  getTokensToApprove,
  shouldRevokeFirst,
} from "../../../utils/allowanceUtils";
import { useGetTokenDecimals } from "../../../hooks/useGetTokenDecimals";
import { useTimedQuoteRefetch } from "../../../hooks/useTimedQuoteRefetch";
import { Allowance } from "../../../store/global/getAllowances";
import { ApprovalStore } from "../../../store/approvals/ApprovalsStore";
import { OrderStatus } from "../../../types/orderStatus";
import { WrapQuoteStatus } from "../../../types/wrapQuote";
import { motion } from "framer-motion";

export const WrappingQuoteInitial = () => {
  const { data: walletClient } = useWalletClient();
  const tokenList = useTokenListByChain();
  const getTokenDecimals = useGetTokenDecimals();
  const { forceRefresh } = useTimedQuoteRefetch();

  const isRefreshingQuote = useRef(false);
  const [hasRefetchedFreshQuote, setHasRefetchedFreshQuote] = useState<boolean>(false);
  const [tx, setTx] = useState<string>("");

  const { network, quote, allowances, theme, address, wrapQuoteStatus } = GlobalStore.useState(
    s => ({
      network: s.network,
      quote: s.quote,
      allowances: s.allowances,
      theme: s.theme,
      address: s.address,
      wrapQuoteStatus: s.wrapQuoteStatus,
    })
  );
  const amountToWrap = getAmountNeededFromQuote(quote, getWrappedSymbolForChain(network));

  const nativeIcon = useMemo(
    () => getTokenIcon(network.nativeCurrency.symbol as Token, tokenList),
    [network.nativeCurrency.symbol, tokenList]
  );
  const wrappedIcon = useMemo(
    () => getTokenIcon(getWrappedSymbolForChain(network), tokenList),
    [network, tokenList]
  );
  const nativeTokenColour = getColourForToken(network.nativeCurrency.symbol as Token, tokenList);

  const DESCRIPTION = `Gas fee trading requires your ${getNameForToken(
    network.nativeCurrency.symbol as Token,
    tokenList
  )} to be converted to
  ${getWrappedSymbolForChain(network)}.`;

  const wrap = useCallback(
    async (
      network: Chain,
      tokenLists: TokenList,
      walletClient: WalletClient<Transport, Chain, Account>,
      amountToWrap: Amount
    ) => {
      GlobalStore.update(s => {
        s.wrapQuoteStatus = WrapQuoteStatus.PENDING_WALLET_CONFIRM;
      });
      const amountToWrapBigInt = parseUnits(
        amountToWrap.value.toString() as `${number}`,
        getTokenDecimals(getWrappedSymbolForChain(network))
      );
      const contract = getContract({
        address: getTokenAddressBySymbol(tokenLists, network, getWrappedSymbolForChain(network)),
        abi: WRAPPED_ABI,
        walletClient,
      });
      try {
        const txHash = await contract.write.deposit({ value: amountToWrapBigInt });
        GlobalStore.update(s => {
          s.wrapQuoteStatus = WrapQuoteStatus.PROCESSING_TRANSACTION;
        });
        setTx(txHash);

        const transaction = await waitForTransaction({
          hash: txHash,
        });

        if (transaction?.status === "success") {
          GlobalStore.update(s => {
            s.wrapQuoteStatus = WrapQuoteStatus.COMPLETED;
          });
          if (tokenList) {
            // Update balances to reflect the new wrapped token balance
            await getBalances.run({
              address: address as Hash,
              tokenList,
              chain: network,
            });
          }
        }
      } catch (e) {
        console.error(`Error submitting wrap transaction`, e);
        GlobalStore.update(s => {
          s.wrapQuoteStatus = WrapQuoteStatus.USER_REJECTED_WRAP;
        });
      }
    },
    [setTx, getTokenDecimals, tokenList, address]
  );

  useEffect(() => {
    if (walletClient && tokenList) {
      const amountToWrap = getAmountNeededFromQuote(quote, getWrappedSymbolForChain(network));
      void wrap(network, tokenList, walletClient, amountToWrap);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletClient]);

  /*
   *  We have logic in reactions.ts which clear the quote when the STORE taker or maker tokens change this is required logic from changing when you are in token selector
   *  We force refresh here and set a flag that we have re-fetched so we know below this hook to send the user to the approval modal if they need to approve.
   *  The reason for doing it here instead of adding a flag of don't clear quote when in "WRAPPING" modal to reactions is to keep logic inside this file
   *  and so we don't unexpectedly not clear quote when we need to.
   * */

  useEffect(() => {
    if (
      quote === undefined &&
      wrapQuoteStatus === WrapQuoteStatus.COMPLETED &&
      !isRefreshingQuote.current
    ) {
      isRefreshingQuote.current = true;
      forceRefresh()
        .then(() => {
          setHasRefetchedFreshQuote(true);
        })
        .catch(e => {
          console.error(".", e);
        })
        .finally(() => {
          isRefreshingQuote.current = false;
        });
    }
  }, [quote, wrapQuoteStatus, forceRefresh]);

  useEffect(() => {
    if (hasRefetchedFreshQuote && wrapQuoteStatus === WrapQuoteStatus.COMPLETED) {
      setTimeout(() => {
        GlobalStore.update(s => {
          const tokensToApprove = getTokensToApprove(cloneDeep(s.quote), allowances);
          const requiredSignatures =
            GlobalStore.getRawState().quote?.requiredSignatures?.length ?? 0;
          console.log(`tokensToApprove`, tokensToApprove);
          console.log(`requiredSignatures`, requiredSignatures);
          const approvalsRequired = tokensToApprove.length > 0 || requiredSignatures > 0;
          if (approvalsRequired) {
            const [revokeFirst, increase] = partition(
              tokensToApprove,
              (entry: Allowance & { needed: Amount }) => shouldRevokeFirst(entry.symbol, allowances)
            );
            ApprovalStore.update(s => {
              s.requiredSignatures = GlobalStore.getRawState().quote?.requiredSignatures ?? [];
              s.tokensToApprove = tokensToApprove;
              s.initialAllowances = allowances;
              s.currentAllowances = allowances;
              s.tokensByFlow = {
                approve: increase,
                revoke: revokeFirst,
              };
              s.permitType = GlobalStore.getRawState().permitType;
            });
          }

          s.orderStatus =
            tokensToApprove.length === 0 && requiredSignatures === 0
              ? OrderStatus.ENTRY
              : OrderStatus.APPROVALS;
        });
      }, 4000);
    }
  }, [hasRefetchedFreshQuote, wrapQuoteStatus, allowances]);

  const WrappedAndNativeIcon = memo(() => {
    const iconVariants = {
      hidden: { opacity: 0, x: -20 },
      visible: { opacity: 1, x: 0 },
    };
    const iconTransition = {
      duration: 0.5,
    };

    return (
      <IconWrapper>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={iconVariants}
          transition={iconTransition}
        >
          <img src={nativeIcon} alt={"Native token icon"} width={60} height={60} />
        </motion.div>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={iconVariants}
          transition={{ ...iconTransition, delay: 0.2 }}
        >
          <ArrowPointingRight stroke={nativeTokenColour} />
        </motion.div>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={iconVariants}
          transition={{ ...iconTransition, delay: 0.4 }}
        >
          <img src={wrappedIcon} alt={"Wrapped token icon"} width={60} height={60} />
        </motion.div>
      </IconWrapper>
    );
  });

  return (
    <>
      <WrappedAndNativeIcon />
      <Text
        textStyle="Title"
        size={"Medium"}
        color={theme.font.primary.default}
        textCss={css`
          margin-top: 30px;
        `}
      >
        Wrap your {capitalize(getNameForToken(network.nativeCurrency.symbol as Token, tokenList))}{" "}
        to trade
      </Text>
      <div
        css={css`
          text-align: center;
        `}
      >
        <Text
          textStyle="Paragraph"
          size="Large"
          textCss={css`
            max-width: 276px;
          `}
          color={theme.font.secondary.default}
        >
          <Text textStyle="Title" size="XS">
            {DESCRIPTION}{" "}
            <LearnMoreLink
              href={NAV_LINKS.TOKENS_THAT_CAN_BE_TRADED}
              target="_blank"
              rel="noreferrer noopener"
            >
              Learn more.
            </LearnMoreLink>
          </Text>
        </Text>
      </div>
      {wrapQuoteStatus === WrapQuoteStatus.USER_REJECTED_WRAP && walletClient && tokenList && (
        <Button
          text={"Retry token wrap"}
          textSize="Large"
          onClick={() => void wrap(network, tokenList, walletClient, amountToWrap)}
          buttonType={"primary"}
          buttonCss={css`
            padding: 19px 18px;
            border-radius: 14px;
            width: 100%;
            justify-content: center;
          `}
        />
      )}
      {wrapQuoteStatus !== WrapQuoteStatus.USER_REJECTED_WRAP && (
        <div
          css={css`
            height: 56px;
            display: flex;
          `}
        >
          <ConfirmInWallet
            isVisible={wrapQuoteStatus === WrapQuoteStatus.PENDING_WALLET_CONFIRM}
            confirmOrApproveText={"Confirm"}
          />
          <ProcessingTransaction
            isVisible={wrapQuoteStatus === WrapQuoteStatus.PROCESSING_TRANSACTION}
            tx={tx}
          />
        </div>
      )}
    </>
  );
};
